/**
 * FlashcardPlayer Component
 * Modern, interactive flashcard viewer for LMS content
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  IconButton,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useFullscreenContent } from './FullscreenContentContext';
import { FullscreenFillContainer } from './ContentFullscreenFrame';

const FlashcardPlayer = ({ flashcards = [], onComplete }) => {
  const isFullscreen = useFullscreenContent();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [reviewed, setReviewed] = useState(new Set());

  const currentCard = flashcards[currentIndex];

  const markReviewed = useCallback((index) => {
    setReviewed((prev) => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((index) => {
      if (index < flashcards.length - 1) {
        markReviewed(index);
        setIsFlipped(false);
        return index + 1;
      }
      markReviewed(index);
      return index;
    });
  }, [flashcards.length, markReviewed]);

  const handlePrev = useCallback(() => {
    setCurrentIndex((index) => {
      if (index > 0) {
        setIsFlipped(false);
        return index - 1;
      }
      return index;
    });
  }, []);

  useEffect(() => {
    if (flashcards.length > 0 && reviewed.size >= flashcards.length) {
      onComplete?.({ cardsReviewed: flashcards.length });
    }
  }, [reviewed.size, flashcards.length, onComplete]);

  useEffect(() => {
    if (flashcards.length === 1 && isFlipped) {
      markReviewed(0);
    }
  }, [flashcards.length, isFlipped, markReviewed]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      } else if (
        event.key === ' ' ||
        event.key === 'Enter' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowDown'
      ) {
        event.preventDefault();
        setIsFlipped((flipped) => !flipped);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev]);

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setReviewed(new Set());
  };

  if (!flashcards.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No flashcards available</Typography>
      </Box>
    );
  }

  const progress = ((currentIndex + 1) / flashcards.length) * 100;

  return (
    <FullscreenFillContainer>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ flexShrink: 0, mb: isFullscreen ? 1.5 : 3 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          Card {currentIndex + 1} of {flashcards.length}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Chip
          label={`${reviewed.size}/${flashcards.length} reviewed`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
      </Stack>

      <Box
        sx={{
          perspective: '1000px',
          flex: isFullscreen ? 1 : undefined,
          minHeight: isFullscreen ? 0 : { xs: 360, md: 460 },
          mb: isFullscreen ? 1.5 : 3,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={() => setIsFlipped(!isFlipped)}
      >
        <Box
          sx={{
            width: '100%',
            flex: isFullscreen ? 1 : undefined,
            height: isFullscreen ? '100%' : { xs: 360, md: 460 },
            minHeight: isFullscreen ? 0 : undefined,
            position: 'relative',
            transformStyle: 'preserve-3d',
            transition: 'transform 0.6s',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
          }}
        >
          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: { xs: 2, sm: 4 },
              borderRadius: isFullscreen ? { xs: 2, sm: 3 } : 1,
            }}
          >
            <Stack spacing={2} alignItems="center" sx={{ maxWidth: '100%' }}>
              <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase' }}>
                Side A
              </Typography>
              <Typography
                variant={isFullscreen ? 'h3' : 'h4'}
                fontWeight={700}
                textAlign="center"
                sx={{ wordBreak: 'break-word', px: { xs: 1, sm: 2 } }}
              >
                {currentCard?.side_a || 'No content'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Click to reveal answer · Enter or Space to flip · ← → to navigate
              </Typography>
            </Stack>
          </Card>

          <Card
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              p: { xs: 2, sm: 4 },
              borderRadius: isFullscreen ? { xs: 2, sm: 3 } : 1,
            }}
          >
            <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxHeight: '100%', overflow: 'auto' }}>
              <Typography variant="caption" sx={{ opacity: 0.8, textTransform: 'uppercase' }}>
                Side B
              </Typography>
              <Box sx={{ width: '100%', textAlign: 'center' }}>
                {(() => {
                  const content = currentCard?.side_b || 'No content';
                  const lines = content.split('\n');

                  return (
                    <Stack spacing={1.5} alignItems="center">
                      {lines.map((line, idx) => {
                        const isCategoryLabel =
                          line.trim().toLowerCase().includes('english') ||
                          line.trim().toLowerCase().includes('basic') ||
                          line.trim().toLowerCase().includes('professional') ||
                          line.trim().toLowerCase().includes('neutral') ||
                          line.trim().toLowerCase().includes('polished');

                        if (isCategoryLabel) {
                          return (
                            <Typography
                              key={idx}
                              sx={{
                                fontSize: '0.5em',
                                fontWeight: 600,
                                color: 'black',
                                textDecoration: 'underline',
                                textDecorationColor: 'black',
                                textDecorationThickness: '2px',
                                textUnderlineOffset: '4px',
                              }}
                            >
                              {line.trim()}
                            </Typography>
                          );
                        }
                        if (line.trim()) {
                          return (
                            <Typography
                              key={idx}
                              variant={isFullscreen ? 'h3' : 'h4'}
                              fontWeight={600}
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {line.trim()}
                            </Typography>
                          );
                        }
                        return null;
                      })}
                    </Stack>
                  );
                })()}
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Click to see question · Enter or Space to flip
              </Typography>
            </Stack>
          </Card>
        </Box>
      </Box>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        sx={{ flexShrink: 0 }}
      >
        <Button
          variant="outlined"
          startIcon={<PrevIcon />}
          onClick={handlePrev}
          disabled={currentIndex === 0}
          size={isFullscreen ? 'medium' : 'medium'}
          sx={{ minWidth: { xs: 'auto', sm: 100 } }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Previous
          </Box>
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={handleReset} size="small">
            <RefreshIcon />
          </IconButton>
          <Button variant="text" onClick={() => setIsFlipped(!isFlipped)} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            {isFlipped ? 'Show Question' : 'Show Answer'}
          </Button>
        </Stack>

        <Button
          variant="contained"
          endIcon={<NextIcon />}
          onClick={handleNext}
          disabled={currentIndex === flashcards.length - 1}
        >
          Next
        </Button>
      </Stack>
    </FullscreenFillContainer>
  );
};

export default FlashcardPlayer;
